"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Positioning() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduced: boolean;
        normal: boolean;
      };
      if (conditions.reduced) {
        gsap.set([".pz-rule", ".pz-kicker", ".pz-word", ".pz-body", ".pz-signature", ".pz-marker"], {
          opacity: 1,
          y: 0,
          scaleX: 1
        });
        gsap.set(".pz-marker-path", {
          strokeDashoffset: 0
        });
        return;
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 78%"
        }
      });
      tl.from(".pz-rule", {
        scaleX: 0,
        transformOrigin: "left center",
        duration: 0.6,
        ease: "power2.out"
      }).from(".pz-kicker", {
        y: 12,
        opacity: 0,
        duration: 0.5,
        ease: "power2.out"
      }, "-=0.2").from(".pz-word", {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.08,
        ease: "cubic-bezier(0.22, 1, 0.36, 1)"
      }, "-=0.2").from(".pz-marker-path", {
        strokeDashoffset: 300,
        duration: 0.7,
        ease: "power2.inOut"
      }, "-=0.3").from(".pz-body", {
        y: 20,
        opacity: 0,
        duration: 0.7,
        ease: "power2.out"
      }, "-=0.4").from(".pz-signature", {
        y: 16,
        opacity: 0,
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.3");
    });
  }, {
    scope: container
  });
  const headingWords = [{
    text: "I",
    italic: false
  }, {
    text: "don't",
    italic: false
  }, {
    text: "use",
    italic: false
  }, {
    text: "templates.",
    italic: false
  }, {
    text: "I",
    italic: false
  }, {
    text: "build",
    italic: false
  }, {
    text: "strategies.",
    italic: true,
    marked: true
  }, {
    text: "that",
    italic: false
  }, {
    text: "drive",
    italic: false
  }, {
    text: "real",
    italic: false
  }, {
    text: "search",
    italic: false
  }, {
    text: "growth",
    italic: false
  }, {
    text: "daily.",
    italic: false
  }];
  return <section data-section-id="22971" id="dundeesearchlandscapeanalysis" ref={container} data-nav="light" className="bg-background text-foreground">
      <div className="mx-auto max-w-[1440px] px-[clamp(1.5rem,5vw,4.5rem)]">
        <Separator className="pz-rule bg-border" />

        <div className="grid grid-cols-1 gap-y-10 py-24 md:grid-cols-12 md:gap-x-6 md:py-[clamp(6rem,12vw,11rem)]">
          {/* Left Column: Eyebrow */}
          <div className="md:col-span-3">
            <div className="md:sticky md:top-24">
              <span className="pz-kicker inline-block font-default text-[14px] font-medium uppercase" style={{
              letterSpacing: "0.18em",
              color: "var(--muted-foreground)"
            }}>
                My approach
              </span>
            </div>
          </div>

          {/* Right Column: Statement Display */}
          <div className="md:col-span-9">
            <h2 className="font-serif font-normal text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
            letterSpacing: "-0.03em",
            lineHeight: "0.96"
          }}>
              {headingWords.map((w, i) => <span key={i} data-index={i} className="pz-word relative mr-[0.28em] inline-block">
                  {w.italic ? <span className="relative italic">
                      {w.text}
                      {w.marked && <svg className="pz-marker pointer-events-none absolute left-0 right-0" style={{
                  bottom: "-0.14em",
                  width: "100%",
                  height: "0.22em",
                  overflow: "visible"
                }} viewBox="0 0 300 22" preserveAspectRatio="none" aria-hidden="true">
                          <path className="pz-marker-path" d="M4 14 C 40 6, 90 18, 140 11 C 195 4, 240 17, 296 9" stroke="var(--accent)" strokeWidth="10" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 320
                  }} />
                        </svg>}
                    </span> : <span>{w.text}</span>}
                </span>)}
            </h2>

            <div className="pz-body mt-10 max-w-[62ch] md:w-9/12">
              <p className="font-default text-foreground" style={{
              fontSize: "clamp(1.25rem, 1.6vw, 1.55rem)",
              letterSpacing: "-0.005em",
              lineHeight: "1.55"
            }}>
                Every project starts with deep research — into your market, 
                your competitors, and your unique business goals. I deliver 
                bespoke SEO strategies designed for your specific needs, 
                ensuring measurable results and sustainable growth.
              </p>
            </div>

            <div className="pz-signature mt-10 flex items-center gap-4">
              <span className="font-serif italic text-foreground" style={{
              fontSize: "23px",
              lineHeight: "1.2"
            }}>
                — Yassine Essaadi, Consultant
              </span>
              <svg width="88" height="28" viewBox="0 0 120 40" fill="none" aria-hidden="true" className="text-foreground">
                <path d="M4 26 C 12 8, 20 34, 30 20 C 38 10, 44 30, 54 18 C 62 8, 70 28, 82 16 C 92 8, 104 22, 116 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                <path d="M60 30 C 72 28, 88 32, 108 30" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" fill="none" opacity="0.6" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>;
}